import type { StepData } from "../Workflow";

export type SearchableStep = Pick<
  StepData,
  "id" | "name" | "connectedApps" | "routeType"
>;

type RectLike = { centerY: number; left: number };

// A step's searchable text: its name, the connectors it uses (display name,
// canonical identifier, and the config slug/type as fallbacks so an unresolved
// connector still matches), and its link (route) type — so a query can find a
// step by the app it talks to or the kind of link it serves, not only by name.
export function stepSearchText(step: SearchableStep): string {
  const parts = [step.name];
  for (const app of step.connectedApps) {
    if (app.connectedApp?.name) parts.push(app.connectedApp.name);
    if (app.connectedApp?.identifier) parts.push(app.connectedApp.identifier);
    parts.push(app.slug, app.appType);
  }
  if (step.routeType) parts.push(step.routeType);
  return parts.join(" ").toLowerCase();
}

// Case-insensitive substring match, returning matches in reading order
// (top-to-bottom, then left-to-right) so stepping through them follows the eye.
// An empty query matches nothing.
export function matchSteps<T extends SearchableStep>(
  steps: readonly T[],
  query: string,
  rects: ReadonlyMap<string, RectLike>
): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return steps
    .filter((s) => stepSearchText(s).includes(q))
    .sort((a, b) => {
      const ra = rects.get(a.id);
      const rb = rects.get(b.id);
      if (!ra || !rb) return 0;
      return ra.centerY - rb.centerY || ra.left - rb.left;
    });
}
